import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import { getConditionTasksSchema } from 'app/pages/loanApplication/tabs/underwriting/tabs/loanConditions/AddConditionsForm.utils';
import {
  applyUploadedFormToTask,
  buildConditionTasks,
  getCreateFormDialogProps,
  getFormViewerDialogProps,
} from 'app/pages/loanApplication/tabs/underwritingCDS/Conditions/AddBrokerConditionForm/AddBrokerConditionForm.utils';
import ConditionTasksTable from 'app/pages/underwriting/conditions/ConditionTasksTable';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';

import { getLoanSpecificForms } from 'store/forms/actions';
import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addConditionTasksAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import { selectTaskTypeDynamicField, selectTaskTypesOptions } from 'store/system/selectors';

import { ConditionsTestIds } from '../Conditions.testIds';

import { initialConditionTasksFormValues } from './AddConditionTasksForm.constants';
import { footerStyles, formWrapperStyles } from './AddConditionTasksForm.styles';

const AddConditionTasksForm = ({ loanConditionId }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();

  const taskTypeOptions = useSelector(selectTaskTypesOptions);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);

  const [isLoading, setIsLoading] = useState(false);

  const {
    form,
    isFormChanged,
    setInForm,
    validationErrors,
    setSchema,
    handleChange,
    validateForm,
    validateField,
  } = useForm(initialConditionTasksFormValues);

  const onCreateNewForm = useCallback(
    ({ taskIndex }) => {
      const abortController = new AbortController();

      const onFinishFormUploading = uploadedForm => {
        dispatch(getLoanSpecificForms({ loanId, applicationId }))
          .catch(Error)
          .finally(() => {
            const newForm = applyUploadedFormToTask({ form, taskIndex, uploadedForm });

            dispatch(
              openDialogAction(
                getFormViewerDialogProps({
                  uploadedForm,
                  onAssignClick: () => setInForm('taskTypeIds', newForm.taskTypeIds),
                }),
              ),
            );
          });
      };

      dispatch(
        openDialogAction(
          getCreateFormDialogProps({ abortController, applicationId, onFinishFormUploading }),
        ),
      );
    },
    [applicationId, dispatch, form, loanId, setInForm],
  );

  const onCancel = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onSubmit = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    setIsLoading(true);

    dispatch(
      addConditionTasksAction({
        loanConditionId,
        tasks: buildConditionTasks({ form, loanId, taskTypeDynamicFieldOptions }),
      }),
    )
      .then(() =>
        Promise.all([
          dispatch(
            getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
          ),
          dispatch(getLoanApplicationTasksAction({ applicationId })),
        ]),
      )
      .then(() => {
        dispatch(showMessageAction({ message: 'Tasks successfully added.', variant: 'success' }));
      })
      .catch(Error)
      .finally(() => {
        dispatch(closeDialogAction());
        setIsLoading(false);
      });
  }, [
    applicationId,
    dispatch,
    form,
    loanConditionId,
    loanId,
    taskTypeDynamicFieldOptions,
    validateForm,
  ]);

  useEffect(() => {
    setSchema(
      getConditionTasksSchema({
        conditionTaskTypes: form.conditionTaskTypes,
        taskTypeDynamicFieldOptions,
      }),
    );
  }, [setSchema, form.conditionTaskTypes, taskTypeDynamicFieldOptions]);

  return (
    <Box>
      <Box sx={formWrapperStyles}>
        <ConditionTasksTable
          isBrokerType
          isTaskAssignmentEnabled
          form={form}
          formName={FormNames.ADD_CONDITION}
          handleChange={handleChange}
          setInForm={setInForm}
          taskTypeOptions={taskTypeOptions}
          tasks={form.taskTypeIds}
          validateField={validateField}
          validationErrors={validationErrors}
          onCreateNewForm={onCreateNewForm}
        />
      </Box>

      <Box sx={footerStyles}>
        <Button
          data-testid={ConditionsTestIds.TASKS_FORM_CANCEL_BUTTON}
          disabled={isLoading}
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid={ConditionsTestIds.TASKS_FORM_SUBMIT_BUTTON}
          disabled={!isFormChanged || isLoading || !form.taskTypeIds.length}
          variant="contained"
          onClick={onSubmit}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

AddConditionTasksForm.propTypes = {
  loanConditionId: PropTypes.string.isRequired,
};

export default memo(AddConditionTasksForm);
